// src/screens/LoginScreen.js - ADJUSTED FOR PHONE & EMAIL LOGIN
import React, { useState, useEffect } from 'react';
// ✨ NEW: Import phone auth methods
import { 
    signInWithEmailAndPassword, 
    sendPasswordResetEmail,
    RecaptchaVerifier,
    signInWithPhoneNumber
} from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import GoogleSignInButton, { OrDivider } from '../components/GoogleSignInButton';
import { auth } from '../firebase';
import MfaCodePrompt, { needsSecondFactor } from '../components/MfaCodePrompt';
// ✨ NEW: Icons for the UI toggle
import { PhoneIcon, MailIcon } from '../components/Icons'; 

export default function LoginScreen() {
    const { setModal } = useAuth();
    const navigate = useNavigate();

    // --- State Management ---
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // ✨ NEW: State for login method and phone auth flow
    const [loginMethod, setLoginMethod] = useState('email'); // 'email' or 'phone'
    const [phoneNumber, setPhoneNumber] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [isCodeSent, setIsCodeSent] = useState(false);
    // Accounts with two-step verification (admins): the sign-in error that
    // MfaCodePrompt completes with the authenticator code.
    const [mfaError, setMfaError] = useState(null);

    // ✨ NEW: Set up reCAPTCHA verifier on component mount
    useEffect(() => {
        try {
            // ✅ FIX: The 'auth' object must be the THIRD argument.
            window.recaptchaVerifier = new RecaptchaVerifier('recaptcha-container-login', {
                'size': 'invisible',
                'callback': () => console.log("reCAPTCHA solved for login.")
            }, auth);
        } catch (error) {
            console.error("Error setting up login reCAPTCHA:", error);
            setModal({ title: "Erro de Configuração", message: "Não foi possível iniciar o verificador de segurança. Por favor, atualize a página." });
        }
    }, []);

    // --- Email Login Handler ---
    const handleEmailLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await signInWithEmailAndPassword(auth, email, password);
            // onAuthStateChanged in useAuth.js will handle navigation
        } catch (err) {
            if (needsSecondFactor(err)) { setMfaError(err); return; }
            let message = "Email ou senha inválidos. Por favor, verifique seus dados.";
            if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
                message = "Email ou senha inválidos. Por favor, verifique seus dados e tente novamente.";
            }
            setModal({ title: "Falha no Login", message });
        } finally {
            setIsLoading(false);
        }
    };
    
    // --- Phone Login Handlers ---
    const handleSendVerificationCode = async () => {
        if (!phoneNumber) {
            setModal({ message: "Por favor, insira um número de telefone." });
            return;
        }
        setIsLoading(true);
        try {
            const appVerifier = window.recaptchaVerifier;
            const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
            window.confirmationResult = confirmationResult;
            setIsCodeSent(true);
            setModal({ title: "Código Enviado", message: `Enviamos um código de verificação para ${phoneNumber}.` });
        } catch (error) {
            console.error("Error sending login verification code:", error);
            setModal({ title: "Erro ao Enviar Código", message: "Não foi possível enviar o código. Verifique se o número está correto e se o usuário existe." });
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyCodeAndLogin = async () => {
        if (verificationCode.length !== 6) {
            setModal({ message: "Por favor, insira o código de 6 dígitos." });
            return;
        }
        setIsLoading(true);
        try {
            await window.confirmationResult.confirm(verificationCode);
            // Login successful. onAuthStateChanged will now take over.
        } catch (error) {
            console.error("Error verifying login code:", error);
            setModal({ title: "Código Inválido", message: "O código inserido está incorreto." });
        } finally {
            setIsLoading(false);
        }
    };

    // --- Password Reset Handler (for email only) ---
    const handlePasswordReset = async () => {
        if (!email) {
            setModal({
                title: "E-mail Necessário",
                message: "Por favor, digite seu e-mail para redefinir a senha."
            });
            return;
        }
        try {
            await sendPasswordResetEmail(auth, email);
            setModal({
                title: "Link Enviado",
                message: "Um link para redefinir sua senha foi enviado para seu e-mail."
            });
        } catch (err) {
            setModal({ title: "Falha na Redefinição", message: "Não foi encontrada nenhuma conta com este e-mail." });
        }
    };

    return (
        <div className="flex flex-col justify-center items-center min-h-screen p-6 bg-gray-50">
            {mfaError && <MfaCodePrompt error={mfaError} onCancel={() => setMfaError(null)} />}
            {/* ✨ NEW: Container for the invisible reCAPTCHA */}
            <div id="recaptcha-container-login" className="absolute"></div>

            <div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-xl">
                <h1 className="text-3xl font-bold text-green-700 mb-2 text-center">Entrar</h1>
                <p className="text-gray-600 mb-6 text-center">Bem-vindo de volta!</p>

                <GoogleSignInButton label="Entrar com Google" onMfaRequired={setMfaError} />
                <OrDivider />

                {/* ✨ NEW: Login Method Toggle */}
	    {/*    <div className="grid grid-cols-2 gap-2 mb-6 rounded-lg bg-gray-100 p-1">
                    <button onClick={() => setLoginMethod('email')} className={`flex items-center justify-center gap-2 p-2 rounded-md font-semibold transition ${loginMethod === 'email' ? 'bg-white shadow text-green-700' : 'text-gray-500'}`}>
                        <MailIcon className="w-5 h-5" /> E-mail
                    </button>
                    <button onClick={() => setLoginMethod('phone')} className={`flex items-center justify-center gap-2 p-2 rounded-md font-semibold transition ${loginMethod === 'phone' ? 'bg-white shadow text-green-700' : 'text-gray-500'}`}>
                        <PhoneIcon className="w-5 h-5" /> Telefone
                    </button>
                </div>
*/}
                {/* Email Login Form */}
                {loginMethod === 'email' && (
                    <form onSubmit={handleEmailLogin} className="w-full space-y-4">
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="E-mail" required className="w-full px-4 py-3 rounded-lg border-gray-300 focus:ring-green-500" />
                        <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Senha" required className="w-full px-4 py-3 rounded-lg border-gray-300 focus:ring-green-500" />
                        <div className="text-right">
                            <button type="button" onClick={handlePasswordReset} className="text-sm font-semibold text-green-600 hover:underline">
                                Esqueceu sua senha?
                            </button>
                        </div>
                        <button type="submit" disabled={isLoading} className="w-full bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700 transition disabled:opacity-50 !mt-6">
                            {isLoading ? 'Entrando...' : 'Entrar com E-mail'}
                        </button>
                    </form>
                )}

                {/* Phone Login Form */}
                {loginMethod === 'phone' && (
                    <div className="w-full space-y-4">
                        {!isCodeSent ? (
                            <>
                                <input type="tel" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} placeholder="Telefone (ex: +5511999998888)" required className="w-full px-4 py-3 rounded-lg border-gray-300 focus:ring-green-500" />
                                <button onClick={handleSendVerificationCode} disabled={isLoading} className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700 transition disabled:opacity-50">
                                    {isLoading ? 'Enviando...' : 'Enviar Código SMS'}
                                </button>
                            </>
                        ) : (
                            <>
                                <input type="text" value={verificationCode} onChange={e => setVerificationCode(e.target.value)} placeholder="Código de 6 dígitos" required maxLength="6" className="w-full px-4 py-3 rounded-lg border-gray-300 focus:ring-green-500 text-center tracking-[0.5em]" />
                                <button onClick={handleVerifyCodeAndLogin} disabled={isLoading} className="w-full bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700 transition disabled:opacity-50">
                                    {isLoading ? 'Verificando...' : 'Entrar com Telefone'}
                                </button>
                                <button onClick={() => setIsCodeSent(false)} className="w-full text-sm text-center text-gray-600 hover:underline">
                                    Usar outro número
                                </button>
                            </>
                        )}
                    </div>
                )}

                <p className="mt-6 text-center text-sm">
                    Não tem uma conta? <button onClick={() => navigate('/signup')} className="text-green-600 font-semibold hover:underline">Crie uma agora</button>
                </p>
            </div>
        </div>
    );
}


